import type { KsefInvoiceLine } from './types';

/**
 * Which formula produced a derived (reconstructed) line amount — drives the "calculated" marker and
 * its tooltip. `null` on a {@link LineAmount} means the value came straight from the XML (source), or
 * that no value could be produced at all (rendered as an em dash).
 */
export type DerivationBasis =
  | 'unit_times_qty' // net = unit net × quantity
  | 'net_div_qty' // unit net = net ÷ quantity
  | 'gross_div_rate' // net = gross ÷ (1 + VAT rate)
  | 'net_plus_vat' // gross = net + VAT (net × (1 + rate))
  | 'gross_minus_net' // VAT = gross − net
  | 'net_times_rate' // VAT = net × VAT rate
  | 'unit_gross_times_qty' // gross = unit gross × quantity (margin line)
  | 'gross_div_qty'; // unit gross = gross ÷ quantity (margin line)

/** One resolved money cell: its value plus where it came from (source when `basis` is null). */
export interface LineAmount {
  value: number | null;
  /** How the value was derived; `null` when it is source-provided (or absent, i.e. `value` is null). */
  basis: DerivationBasis | null;
}

export interface LineAmounts {
  unitNet: LineAmount;
  /** Gross unit price (`P_9B`) — only a margin-scheme line carries one; net-priced lines leave it empty. */
  unitGross: LineAmount;
  net: LineAmount;
  vat: LineAmount;
  gross: LineAmount;
}

const UNKNOWN: LineAmount = { value: null, basis: null };
const source = (value: number): LineAmount => ({ value, basis: null });
const derived = (value: number, basis: DerivationBasis): LineAmount => ({ value, basis });

/**
 * Round to 2 decimals HALF_UP, away from zero — mirrors the issuing path's money rounding
 * (see the KSeF invoice VAT-rounding rule) so a reconstructed amount matches what a real issue
 * would have written. The small epsilon counters binary-float values that land a hair below `.5`
 * (e.g. `1.005 * 100 === 100.49999999999999`, which would otherwise truncate to `1.00`).
 */
export function roundMoney(n: number): number {
  const scaled = Math.floor(Math.abs(n) * 100 + 0.5 + 1e-9);
  return (n < 0 ? -scaled : scaled) / 100;
}

/** The line's VAT rate as a number, or null when it is non-numeric (`zw`, `oo`, `np`, …). */
export function numericRate(line: KsefInvoiceLine): number | null {
  const rate = line.vatRate != null ? Number(line.vatRate) : NaN;
  return Number.isFinite(rate) ? rate : null;
}

/**
 * Whether this line is priced under the margin scheme: gross unit price (`P_9B`) and gross line value
 * (`P_11A`), no net (`P_9A`/`P_11`) and no rate (`P_12`).
 *
 * Detected per line but *gated on the invoice*, because FA(3) puts `P_PMarzy` in the invoice-level
 * `Adnotacje` while the amount structure lives on each row — an invoice may legally carry margin rows
 * beside ordinary rated ones, so the flag alone cannot decide a row, and the rate alone cannot decide
 * an invoice. What identifies a margin row is what it must *omit* (art. 106e ust. 3: no rate, no net)
 * on a document that claims the scheme.
 *
 * `P_9B` is deliberately NOT a signal. The schema documents `P_9B`/`P_11A` as the art. 106e ust. 7 i 8
 * gross calculation method, not the margin scheme, and issuers routinely emit a gross unit price
 * alongside `P_11`, `P_11Vat` and `P_12` on wholly ordinary invoices (one carrying `P_PMarzyN`, the
 * negative marker, at that). Treating it as the signal badged those rows "Marża" and blanked their
 * stated net and VAT.
 */
export function isMarginLine(line: KsefInvoiceLine, invoiceMargin = false): boolean {
  return invoiceMargin && line.vatRate == null && line.netValue == null && line.unitNetPrice == null;
}

/**
 * Whether the invoice is margin-priced *throughout* — the ordinary margin invoice, as opposed to one
 * that mixes margin positions with ordinary rated ones. The distinction drives what the detail view may
 * omit: a wholly-margin invoice states no net and no VAT anywhere, so those columns and amount pills
 * carry nothing; a mixed one states both for real, on its rated rows.
 */
export function isMarginOnly(lines: KsefInvoiceLine[], invoiceMargin: boolean): boolean {
  return invoiceMargin && lines.length > 0 && lines.every((line) => isMarginLine(line, true));
}

/**
 * Resolve one invoice line's money cells (unit net, unit gross, net, VAT, gross), filling gaps by
 * derivation and recording each value's provenance so the UI can render reconstructed numbers
 * visibly distinct from source ones. The XML is the legal artifact; anything we compute is our
 * interpretation, so a derived figure must never masquerade as source data.
 *
 * Inference order (each step may feed the next, but never circularly — unit-net is only derived
 * from net when net did not itself come from unit-net):
 *   net      ← source · unit × qty · gross ÷ (1 + rate)
 *   unit net ← source · net ÷ qty
 *   gross    ← source · net × (1 + rate)
 *   VAT      ← gross − net (source gross) · net × rate      (a line carries no source VAT field)
 *
 * A margin-scheme line ({@link isMarginLine}, `margin` being the invoice-level `PMarzy` flag) is a
 * different shape, not a lossy one: the VAT sits on the dealer's margin, so the line discloses a gross
 * unit price and a gross value and *nothing else*. Its own inference runs on those two —
 *   gross     ← source · unit gross × qty
 *   unit gross← source · gross ÷ qty
 * — and net, unit net and VAT all stay em dashes. Reconstructing any of them (by grossing down from a
 * rate, or calling the gross figure a net one) would be actively wrong, not helpful.
 */
export function deriveLineAmounts(line: KsefInvoiceLine, margin = false): LineAmounts {
  const qty = line.quantity ?? null;
  const rawGross = line.grossValue ?? null;

  if (isMarginLine(line, margin)) {
    const rawUnitGross = line.unitGrossPrice ?? null;
    let gross: LineAmount;
    if (rawGross != null) gross = source(rawGross);
    else if (rawUnitGross != null && qty != null)
      gross = derived(roundMoney(rawUnitGross * qty), 'unit_gross_times_qty');
    else gross = UNKNOWN;

    let unitGross: LineAmount;
    if (rawUnitGross != null) unitGross = source(rawUnitGross);
    else if (gross.value != null && qty != null && qty !== 0)
      unitGross = derived(roundMoney(gross.value / qty), 'gross_div_qty');
    else unitGross = UNKNOWN;

    return { unitNet: UNKNOWN, unitGross, net: UNKNOWN, vat: UNKNOWN, gross };
  }

  const rate = numericRate(line);
  const rawUnit = line.unitNetPrice ?? null;
  const rawNet = line.netValue ?? null;

  let net: LineAmount;
  if (rawNet != null) net = source(rawNet);
  else if (rawUnit != null && qty != null) net = derived(roundMoney(rawUnit * qty), 'unit_times_qty');
  else if (rawGross != null && rate != null)
    net = derived(roundMoney(rawGross / (1 + rate / 100)), 'gross_div_rate');
  else net = UNKNOWN;

  let unitNet: LineAmount;
  if (rawUnit != null) unitNet = source(rawUnit);
  else if (net.value != null && qty != null && qty !== 0)
    unitNet = derived(roundMoney(net.value / qty), 'net_div_qty');
  else unitNet = UNKNOWN;

  let gross: LineAmount;
  if (rawGross != null) gross = source(rawGross);
  else if (net.value != null && rate != null)
    gross = derived(roundMoney(net.value * (1 + rate / 100)), 'net_plus_vat');
  else gross = UNKNOWN;

  // A line has no source VAT element, so VAT is always our computation — prefer source gross − net,
  // else net × rate.
  let vat: LineAmount;
  if (rawGross != null && net.value != null)
    vat = derived(roundMoney(rawGross - net.value), 'gross_minus_net');
  else if (net.value != null && rate != null)
    vat = derived(roundMoney((net.value * rate) / 100), 'net_times_rate');
  else vat = UNKNOWN;

  return { unitNet, unitGross: UNKNOWN, net, vat, gross };
}
